/**
 * Frontend mirror of backend/config/plans.js. Holds prices, seats, traveler
 * caps and feature flags only — all display copy lives in the i18n locale
 * files, keyed by plan id, so the pricing surfaces stay translatable.
 *
 * Keep amounts in sync with the backend, which bills in cents.
 */

export const UNLIMITED = -1;
export const CONTACT_SALES = 'contact_sales';
export const PRICING_VERSION = '2026_v1';
export const PARTNER_SALES_EMAIL = 'info@dejavunomad.com';
export const partnerSalesMailto = () => `mailto:${PARTNER_SALES_EMAIL}`;

export const PARTNER_TRIAL_DAYS = 14;
export const TRIP_PASS_PROMPT_DAYS_BEFORE = 7;
export const EVENTS_SKU_MIN_TRAVELLERS = 30;
export const TRAVELER_CREDIT_PACKS = [10, 25, 50, 100];
export const AGENCY_EXTRA_SEAT_MONTHLY = 15;
export const PARTNER_PROFESSIONAL_ONBOARDING_FEE = 499;
export const PARTNER_PROFESSIONAL_ONBOARDING_FEE_CENTS = 49900;
export const PARTNER_ONBOARDING_TIER_TYPE = 'partner_professional_onboarding';
export const PARTNER_DEVELOPER_API_MONTHLY = 50;
export const PARTNER_DEVELOPER_API_MONTHLY_CENTS = 5000;
export const PARTNER_DEVELOPER_API_TIER_TYPE = 'partner_developer_api';
export const PARTNER_DEVELOPER_API_SANDBOX_INCLUDED = 5000;
export const PARTNER_DEVELOPER_API_PRODUCTION_INCLUDED = 25000;
export const PARTNER_DEVELOPER_API_OVERAGE_UNIT = 10000;
export const PARTNER_DEVELOPER_API_OVERAGE_CENTS = 1000;

export const partnerOnboardingFeatureKeys = [
  'partnerPlans.onboardingCard.featureAccount',
  'partnerPlans.onboardingCard.featureTeam',
  'partnerPlans.onboardingCard.featurePackages',
  'partnerPlans.onboardingCard.featureTraining'
];

export const partnerDeveloperApiFeatureKeys = [
  'partnerPlans.developerApi.featureConnect',
  'partnerPlans.developerApi.featureKeys',
  'partnerPlans.developerApi.featurePackagesFree',
  'partnerPlans.developerApi.featureWebhooks',
  'partnerPlans.developerApi.featureOverage'
];

export const ATTRIBUTION = {
  PARTNER_SOURCED: 'PARTNER_SOURCED',
  DVN_SOURCED: 'DVN_SOURCED',
  AFFILIATE_SOURCED: 'AFFILIATE_SOURCED',
  REFERRAL_SOURCED: 'REFERRAL_SOURCED',
  MARKETPLACE: 'marketplace',
  PARTNER_CLIENT: 'partner_client'
};

export const PAYMENT_PROCESSING_MODE = {
  PARTNER_DIRECT: 'partner_direct',
  DVN_CHECKOUT: 'dvn_checkout',
  STRIPE_CONNECT: 'stripe_connect'
};

export const CONSUMER_PLANS = [
  {
    id: 'trip_pass',
    amount: 19,
    billing: 'one_time',
    unitKey: 'pricingPage.perTrip'
  },
  {
    id: 'passport',
    amount: 49,
    billing: 'year',
    unitKey: 'pricingPage.perYear',
    bestValue: true
  },
  {
    id: 'events',
    billing: CONTACT_SALES,
    minTravellers: EVENTS_SKU_MIN_TRAVELLERS
  }
];

export const CONSUMER_ADDONS = [
  { id: 'ai_concierge', amount: 35, billing: 'one_time' },
  { id: 'white_glove_service', amount: 50, billing: 'one_time' }
];

export const PARTNER_PLANS = [
  {
    id: 'free',
    tierType: null,
    monthly: 0,
    yearly: 0,
    seats: 1,
    travelers: 15,
    extraTraveler: null,
    extraSeat: null,
    concurrentClientTrips: 3,
    maxGroupTrips: 0,
    introductionFeeRate: 10,
    free: true,
    selfServe: true,
    hasMarketplaceListing: true,
    hasFeaturedPlacement: false,
    hasBrandedItineraries: false,
    hasWhiteLabel: false,
    hasTeamReporting: false,
    hasGooglePlacesBrowse: false,
    hasClientInvite: false,
    hasAiPlanning: false,
    aiAllowance: 0,
    hasApiSandbox: false,
    hasProductionApi: false
  },
  {
    id: 'pro',
    tierType: 'partner_plan_pro',
    monthly: 29,
    yearly: 290,
    seats: 1,
    travelers: 50,
    extraTraveler: 1,
    extraSeat: null,
    maxGroupTrips: UNLIMITED,
    introductionFeeRate: 8,
    recommended: true,
    selfServe: true,
    trialDays: PARTNER_TRIAL_DAYS,
    hasMarketplaceListing: true,
    hasFeaturedPlacement: false,
    hasBrandedItineraries: false,
    hasWhiteLabel: false,
    hasTeamReporting: false,
    hasGooglePlacesBrowse: false,
    hasClientInvite: false,
    hasAiPlanning: false,
    aiAllowance: 0,
    hasApiSandbox: false,
    hasProductionApi: false
  },
  {
    id: 'group',
    tierType: 'partner_plan_group',
    monthly: 79,
    yearly: 790,
    seats: 3,
    travelers: 200,
    extraTraveler: 0.75,
    extraSeat: null,
    maxGroupTrips: UNLIMITED,
    introductionFeeRate: 6,
    selfServe: true,
    trialDays: PARTNER_TRIAL_DAYS,
    hasMarketplaceListing: true,
    hasFeaturedPlacement: false,
    hasBrandedItineraries: false,
    hasWhiteLabel: false,
    hasTeamReporting: false,
    reportingLevel: 'basic',
    hasGooglePlacesBrowse: false,
    hasClientInvite: false,
    hasAiPlanning: true,
    aiAllowance: 200,
    hasApiSandbox: false,
    hasProductionApi: false
  },
  {
    id: 'agency',
    tierType: 'partner_plan_agency',
    monthly: 149,
    yearly: 1490,
    seats: 5,
    travelers: 500,
    extraTraveler: 0.5,
    extraSeat: AGENCY_EXTRA_SEAT_MONTHLY,
    maxGroupTrips: UNLIMITED,
    introductionFeeRate: 5,
    selfServe: true,
    trialDays: PARTNER_TRIAL_DAYS,
    hasMarketplaceListing: true,
    hasFeaturedPlacement: true,
    hasBrandedItineraries: false,
    hasWhiteLabel: false,
    hasTeamReporting: true,
    reportingLevel: 'advanced',
    hasGooglePlacesBrowse: true,
    hasClientInvite: false,
    hasAiPlanning: true,
    aiAllowance: 500,
    hasApiSandbox: true,
    hasProductionApi: false
  },
  {
    id: 'bespoke',
    tierType: null,
    billing: CONTACT_SALES,
    seats: UNLIMITED,
    travelers: UNLIMITED,
    extraTraveler: null,
    extraSeat: null,
    introductionFeeRate: null,
    negotiatedFeeRange: [3, 6],
    maxGroupTrips: UNLIMITED,
    selfServe: false,
    hasMarketplaceListing: false,
    hasFeaturedPlacement: false,
    hasBrandedItineraries: true,
    hasWhiteLabel: true,
    hasTeamReporting: true,
    hasGooglePlacesBrowse: true,
    hasClientInvite: true,
    hasAiPlanning: true,
    aiAllowance: UNLIMITED,
    hasApiSandbox: true,
    hasProductionApi: true
  }
];

/**
 * Partner comparison rows, read straight off the plan entitlements rather than
 * written out, so a tier can never advertise a capability its flags don't grant.
 *
 * `value` returns true, false, or a string to print. Row labels live in i18n
 * under `pricingPage.compare.partner.<id>`. Branding cells are keys under
 * `pricingPage.compare.branding.<id>`.
 */
/** Self-serve tiers shown as columns on the launch pricing table. */
export const PARTNER_LAUNCH_PLANS = PARTNER_PLANS.filter((plan) => plan.selfServe);

const launchAiPlanningCell = (plan) => {
  if (plan.hasAiPlanning) return true;
  if (plan.id === 'free') return 'aiPerTrip';
  if (plan.id === 'pro') return 'aiLimitedIncluded';
  return false;
};

const launchMarketplaceCell = (plan) => {
  if (plan.hasFeaturedPlacement) return 'featured';
  return Boolean(plan.hasMarketplaceListing);
};

const launchReportingCell = (plan) => plan.reportingLevel || false;

const launchClientTripsCell = (plan) => {
  if (plan.concurrentClientTrips) return { type: 'activeConcurrent', count: plan.concurrentClientTrips };
  return 'unlimited';
};

/** Launch pricing table rows — order matches the public partner pricing page. */
export const PARTNER_LAUNCH_COMPARISON = [
  {
    id: 'monthly',
    value: (plan, { interval, t }) => {
      if (plan.free) return t('pricingPage.free');
      const amount = interval === 'yearly' ? plan.yearly : plan.monthly;
      return `$${amount}`;
    }
  },
  {
    id: 'travelers',
    value: (plan) => (isUnlimited(plan.travelers) ? 'unlimited' : String(plan.travelers))
  },
  {
    id: 'seats',
    value: (plan) => (isUnlimited(plan.seats) ? 'unlimited' : String(plan.seats))
  },
  { id: 'clientTrips', value: (plan) => launchClientTripsCell(plan) },
  {
    id: 'groupTrips',
    value: (plan) => plan.maxGroupTrips !== 0
  },
  { id: 'aiPlanning', value: (plan) => launchAiPlanningCell(plan) },
  { id: 'marketplace', value: (plan) => launchMarketplaceCell(plan) },
  { id: 'reporting', value: (plan) => launchReportingCell(plan) },
  { id: 'apiSandbox', value: (plan) => Boolean(plan.hasApiSandbox) },
  {
    id: 'dvnSourcedFee',
    value: (plan) => (plan.introductionFeeRate == null ? null : `${plan.introductionFeeRate}%`)
  },
  { id: 'yourClients', value: () => '0%' }
];

export const resolveLaunchComparisonCell = (value, t) => {
  if (typeof value === 'boolean') return value;
  if (value == null) return '—';
  if (value === 'unlimited') return t('pricingPage.compare.unlimited');
  if (value === 'aiPerTrip') return t('pricingPage.compare.aiPerTrip');
  if (value === 'aiLimitedIncluded') return t('pricingPage.compare.aiLimitedIncluded');
  if (value === 'featured') return t('pricingPage.compare.marketplaceFeatured');
  if (value === 'basic') return t('pricingPage.compare.reportingBasic');
  if (value === 'advanced') return t('pricingPage.compare.reportingAdvanced');
  if (typeof value === 'object' && value.type === 'activeConcurrent') {
    return t('pricingPage.compare.activeClientTrips', { count: value.count });
  }
  return value;
};

export const buildPartnerLaunchComparison = ({ t, interval, partnerName }) => ({
  columns: PARTNER_LAUNCH_PLANS.map((plan) => ({
    id: plan.id,
    label: partnerName(plan.id),
    highlight: Boolean(plan.recommended)
  })),
  rows: PARTNER_LAUNCH_COMPARISON.map((row) => ({
    id: row.id,
    label: row.id === 'monthly'
      ? t(`pricingPage.compare.partner.${interval === 'yearly' ? 'yearly' : 'monthly'}`)
      : t(`pricingPage.compare.partner.${row.id}`),
    cells: PARTNER_LAUNCH_PLANS.map((plan) => resolveLaunchComparisonCell(
      row.value(plan, { interval, t }),
      t
    ))
  }))
});

/** @deprecated Use PARTNER_LAUNCH_COMPARISON for the public pricing table. */
export const PARTNER_COMPARISON = PARTNER_LAUNCH_COMPARISON;

export const SALES_INQUIRY_TYPES = {
  BESPOKE: 'bespoke',
  HOST_AGENCY: 'host_agency',
  EVENTS: 'events'
};

export const getPartnerPlan = (id) => PARTNER_PLANS.find((plan) => plan.id === id) || PARTNER_PLANS[0];

export const partnerPlanMetaKey = (plan) => {
  const id = plan.id === 'free' ? 'starter' : plan.id;
  return `pricingPage.partner.${id}Meta`;
};

export const partnerPlanMarketplaceFee = (plan, t) => {
  if (plan.introductionFeeRate == null) return null;
  return t('pricingPage.partner.marketplaceFee', { rate: plan.introductionFeeRate });
};

export const getConsumerPlan = (id) => CONSUMER_PLANS.find((plan) => plan.id === id) || null;

export const brandingDepth = (plan) => {
  if (plan.id === 'bespoke') return 'customDomain';
  if (plan.hasWhiteLabel) return 'whiteLabel';
  if (plan.hasBrandedItineraries) return 'logoColours';
  return 'house';
};

export const partnerPlanFeatures = (plan, t) => {
  if (plan.id === 'bespoke') {
    return [
      t('partnerPlans.feature.whiteLabel'),
      t('partnerPlans.feature.customDomain'),
      t('partnerPlans.feature.sso'),
      t('partnerPlans.feature.apiAccess'),
      t('partnerPlans.feature.customUsage'),
      t('partnerPlans.feature.negotiatedMarketplaceFee'),
      t('partnerPlans.feature.dedicatedOnboarding')
    ];
  }

  const features = [];
  const groupTripsAllowed = plan.maxGroupTrips === UNLIMITED || plan.maxGroupTrips === -1;

  if (plan.concurrentClientTrips) {
    features.push(t('partnerPlans.feature.concurrentClientTrips', { count: plan.concurrentClientTrips }));
  } else {
    features.push(t('partnerPlans.feature.unlimitedClientTrips'));
  }

  if (groupTripsAllowed) {
    features.push(t('partnerPlans.feature.unlimitedGroupTrips'));
  } else {
    features.push(t('partnerPlans.feature.noGroupTrips'));
  }

  if (isUnlimited(plan.travelers)) {
    features.push(t('partnerPlans.feature.unlimitedTravelers'));
  } else {
    features.push(t('partnerPlans.feature.travelersPerMonth', { count: plan.travelers }));
  }

  features.push(
    plan.seats === 1
      ? t('partnerPlans.feature.oneSeat')
      : t('partnerPlans.feature.seats', { count: plan.seats }),
    t('partnerPlans.feature.unlimitedPackages')
  );

  if (plan.hasAiPlanning) {
    features.push(t('partnerPlans.feature.aiIncluded'));
  } else if (plan.id === 'free') {
    features.push(t('partnerPlans.feature.aiPayPerTrip'));
  } else if (plan.id === 'pro') {
    features.push(t('partnerPlans.feature.aiLimitedIncluded'));
  }

  if (plan.hasFeaturedPlacement) {
    features.push(t('partnerPlans.feature.featuredPlacement'));
  } else if (plan.hasMarketplaceListing) {
    features.push(t('partnerPlans.feature.marketplaceListing'));
  }

  if (plan.reportingLevel === 'advanced') {
    features.push(t('partnerPlans.feature.reportingAdvanced'));
  } else if (plan.reportingLevel === 'basic') {
    features.push(t('partnerPlans.feature.reportingBasic'));
  }

  if (plan.introductionFeeRate != null) {
    features.push(t('partnerPlans.feature.dvnSourcedFee', { rate: plan.introductionFeeRate }));
  }
  features.push(t('partnerPlans.feature.ownClients'));

  if (plan.hasApiSandbox) {
    features.push(t('partnerPlans.feature.apiSandbox'));
    features.push(t('partnerPlans.feature.apiKeysWebhooks'));
    features.push(t('partnerPlans.feature.apiSandboxQuota'));
  }

  return features;
};

export const publicFeePlans = () => PARTNER_PLANS.filter((plan) => plan.introductionFeeRate != null);

export const introductionFeeLadder = (nameFor) =>
  publicFeePlans().map((plan) => `${nameFor(plan.id)} ${plan.introductionFeeRate}%`).join(' · ');

export const introductionFeeBounds = () => {
  const rates = publicFeePlans().map((plan) => plan.introductionFeeRate);
  return { min: Math.min(...rates), max: Math.max(...rates) };
};

export const introductionFeeCopyVars = () => {
  const { min, max } = introductionFeeBounds();
  return { feeMin: min, feeMax: max, min, max };
};

/**
 * Same math as backend/config/plans.js: percent of booking, never more than
 * the booking itself. No $25 floor and no $1000 cap. Rate 0 stays 0.
 */
export const computeIntroductionFeeAmount = (totalPrice, ratePercent) => {
  const totalCents = Math.round((Number(totalPrice) || 0) * 100);
  const rate = Number(ratePercent) || 0;
  if (totalCents <= 0 || rate <= 0) return 0;

  const rawCents = Math.round((totalCents * rate) / 100);
  return Math.min(totalCents, rawCents) / 100;
};

export const estimateProcessingFee = (totalPrice, mode) => {
  if (mode !== PAYMENT_PROCESSING_MODE.DVN_CHECKOUT) return 0;
  const totalCents = Math.round((Number(totalPrice) || 0) * 100);
  if (totalCents <= 0) return 0;
  return Math.round((totalCents * 2.9) / 100 + 30) / 100;
};

export const normalizeAttribution = (attribution) => {
  if (attribution === ATTRIBUTION.PARTNER_SOURCED || attribution === ATTRIBUTION.PARTNER_CLIENT) {
    return ATTRIBUTION.PARTNER_SOURCED;
  }
  if (attribution === ATTRIBUTION.AFFILIATE_SOURCED || attribution === ATTRIBUTION.REFERRAL_SOURCED) {
    return attribution;
  }
  return ATTRIBUTION.DVN_SOURCED;
};

export const travelerCreditPackAmount = (planId, packSize) => {
  const plan = getPartnerPlan(planId);
  if (!plan?.extraTraveler) return null;
  const size = Number(packSize) || 0;
  if (!TRAVELER_CREDIT_PACKS.includes(size)) return null;
  return plan.extraTraveler * size;
};

export const isUnlimited = (limit) => limit === UNLIMITED || limit == null;
